/**
 * New Authentication Redux Slice
 * Implements sequential, state-driven authentication workflow
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, AuthError, AuthErrorType, TokenStatus, User, Tenant, Permission, LoginSuccessPayload, StoredAccount, AccountSwitchPayload, AddAccountPayload, RemoveAccountPayload, AccountMetadataUpdate, ValidationPayload } from '../../types/auth';
import { logger } from '../../utils/logger';

// Initial state following the design specification
const initialState: AuthState = {
  // Authentication status
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  
  // User information
  user: null,
  
  // Tenant context
  tenants: [],
  currentTenant: null,
  tenantPermissions: [],
  
  // Multi-account management (migrated from legacy system)
  activeAccountId: null,
  availableAccounts: [],
  isAccountSwitching: false,
  accountSwitchError: null,
  
  // Account management operations
  isAddingAccount: false,
  addAccountError: null,
  isRemovingAccount: false,
  removeAccountError: null,
  
  // Account validation
  lastValidationTime: null,
  validationErrors: {},
  
  // Error handling
  error: null,
  lastOperation: null,
  
  // Token status
  tokenStatus: TokenStatus.Invalid
};

/**
 * Helper function to create AuthError
 */
const createAuthError = (
  type: AuthErrorType,
  message: string,
  userMessage: string,
  code: string,
  recoverable: boolean = false,
  retryable: boolean = false,
  context?: Record<string, any>
): AuthError => ({
  type,
  message,
  userMessage,
  code,
  recoverable,
  retryable,
  context,
  timestamp: Date.now() // Use number timestamp instead of Date object
});

/**
 * Authentication Slice
 * Implements centralized state management with sequential operations
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Workflow State Management
    startLogin: (state) => {
      logger.auth('Starting login workflow');
      state.isLoading = true;
      state.error = null;
      state.lastOperation = 'login';
    },

    startLogout: (state) => {
      logger.auth('Starting logout workflow');
      state.isLoading = true;
      state.error = null;
      state.lastOperation = 'logout';
    },

    startTenantSwitch: (state, action: PayloadAction<string>) => {
      logger.auth('Starting tenant switch workflow', { tenantId: action.payload });
      state.isLoading = true;
      state.error = null;
      state.lastOperation = 'tenant_switch';
    },

    startAccountSwitchWorkflow: (state, action: PayloadAction<string>) => {
      logger.auth('Starting account switch workflow', { accountId: action.payload });
      state.isLoading = true;
      state.error = null;
      state.lastOperation = 'account_switch';
    },

    startTokenRefresh: (state) => {
      logger.auth('Starting token refresh workflow');
      state.tokenStatus = TokenStatus.Refreshing;
      state.error = null;
      state.lastOperation = 'token_refresh';
    },

    // Authentication Success
    loginSuccess: (state, action: PayloadAction<LoginSuccessPayload>) => {
      logger.auth('Login workflow completed successfully');
      const { user, tenants, currentTenant, permissions } = action.payload;
      
      // Debug logging for user data
      logger.auth('Storing user data in Redux state', {
        userId: user?.id,
        userName: user?.name,
        userEmail: user?.email,
        hasUser: !!user,
        hasName: !!user?.name,
        hasEmail: !!user?.email
      });
      
      state.isAuthenticated = true;
      state.isLoading = false;
      state.isInitialized = true;
      state.user = user;
      state.tenants = tenants;
      state.currentTenant = currentTenant;
      state.tenantPermissions = permissions;
      state.tokenStatus = TokenStatus.Valid;
      state.error = null;
      state.lastOperation = null;
    },

    // Permissions Management (separate from JWT)
    setUserPermissions: (state, action: PayloadAction<{ permissions: string[], roles: string[] }>) => {
      logger.auth('Setting user permissions from API');
      const { permissions, roles } = action.payload;
      
      // Store permissions in user object - keep original string format for compatibility
      if (state.user) {
        state.user.systemPermissions = permissions; // Store as strings for compatibility
        state.user.roles = roles;
      }
      
      // Also store in tenantPermissions as structured format for backward compatibility
      state.tenantPermissions = permissions.map(p => ({
        resource: p.split('.')[0] || p,
        actions: [p.split('.')[1] || 'access']
      }));
    },

    clearUserPermissions: (state) => {
      logger.auth('Clearing user permissions');
      if (state.user) {
        state.user.systemPermissions = [];
        state.user.roles = [];
      }
      state.tenantPermissions = [];
    },

    // Authentication Failure
    loginFailure: (state, action: PayloadAction<AuthError>) => {
      logger.auth('Login workflow failed', action.payload);
      state.isAuthenticated = false;
      state.isLoading = false;
      state.isInitialized = true;
      state.user = null;
      state.tenants = [];
      state.currentTenant = null;
      state.tenantPermissions = [];
      state.tokenStatus = TokenStatus.Invalid;
      state.error = action.payload;
      state.lastOperation = null;
    },

    // Logout Success
    logoutSuccess: (_state) => {
      logger.auth('Logout workflow completed successfully');
      return { ...initialState, isInitialized: true };
    },

    // Logout Failure
    logoutFailure: (state, action: PayloadAction<AuthError>) => {
      logger.auth('Logout workflow failed', action.payload);
      // Even if logout fails, clear sensitive data
      state.isAuthenticated = false;
      state.isLoading = false;
      state.user = null;
      state.tenants = [];
      state.currentTenant = null;
      state.tenantPermissions = [];
      // Clear multi-account state
      state.activeAccountId = null;
      state.availableAccounts = [];
      state.isAccountSwitching = false;
      state.accountSwitchError = null;
      state.isAddingAccount = false;
      state.addAccountError = null;
      state.isRemovingAccount = false;
      state.removeAccountError = null;
      state.lastValidationTime = null;
      state.validationErrors = {};
      state.tokenStatus = TokenStatus.Invalid;
      state.error = action.payload;
      state.lastOperation = null;
    },

    // User Data Management
    setUser: (state, action: PayloadAction<User>) => {
      logger.auth('Setting user data');
      state.user = action.payload;
    },

    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      logger.auth('Updating user data');
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },

    // Preferences are now managed in userPreferencesSlice
    // Removed: setPreferences, updatePreferences, preferencesLoadFailure

    // Tenant Management
    setTenants: (state, action: PayloadAction<Tenant[]>) => {
      logger.auth('Setting tenants list');
      state.tenants = action.payload;
    },

    setCurrentTenant: (state, action: PayloadAction<Tenant>) => {
      logger.auth('Setting current tenant', { tenantId: action.payload.id });
      state.currentTenant = action.payload;
      state.isLoading = false;
      state.lastOperation = null;
    },

    setTenantPermissions: (state, action: PayloadAction<Permission[]>) => {
      logger.auth('Setting tenant permissions');
      state.tenantPermissions = action.payload;
    },

    tenantSwitchSuccess: (state, action: PayloadAction<{ tenant: Tenant; permissions: Permission[] }>) => {
      logger.auth('Tenant switch completed successfully');
      const { tenant, permissions } = action.payload;
      state.currentTenant = tenant;
      state.tenantPermissions = permissions;
      state.isLoading = false;
      state.error = null;
      state.lastOperation = null;
    },

    tenantSwitchFailure: (state, action: PayloadAction<AuthError>) => {
      logger.auth('Tenant switch failed', action.payload);
      state.isLoading = false;
      state.error = action.payload;
      state.lastOperation = null;
    },

    accountSwitchSuccess: (state, action: PayloadAction<LoginSuccessPayload>) => {
      logger.auth('Account switch completed successfully');
      const { user, tenants, currentTenant, permissions } = action.payload;
      
      // Debug logging for user data during account switch
      logger.auth('Storing user data in Redux state during account switch', {
        userId: user?.id,
        userName: user?.name,
        userEmail: user?.email,
        hasUser: !!user,
        hasName: !!user?.name,
        hasEmail: !!user?.email
      });
      
      state.isAuthenticated = true;
      state.isLoading = false;
      state.isInitialized = true;
      state.user = user;
      state.tenants = tenants;
      state.currentTenant = currentTenant;
      state.tenantPermissions = permissions;
      state.tokenStatus = TokenStatus.Valid;
      state.error = null;
      // Keep lastOperation as 'account_switch' so components can detect the switch completion
      state.lastOperation = 'account_switch_completed';
    },

    accountSwitchFailure: (state, action: PayloadAction<AuthError>) => {
      logger.auth('Account switch failed', action.payload);
      state.isLoading = false;
      state.error = action.payload;
      state.lastOperation = null;
    },

    // Account removal notification - doesn't change auth state but notifies UI
    accountRemoved: (_state, action: PayloadAction<{ accountId: string; wasActive: boolean }>) => {
      logger.auth('Account removed notification', action.payload);
      // This action is mainly for UI notification purposes
      // The actual account data is managed in local storage and useAuth hook
      // If the removed account was active, the auth state would have been updated by switchAccount or logout
    },

    tenantLoadFailure: (state, action: PayloadAction<string>) => {
      logger.auth('Tenant load failed', action.payload);
      const error = createAuthError(
        AuthErrorType.TenantDataUnavailable,
        action.payload,
        'Failed to load tenant information. Some features may be limited.',
        'TENANT_LOAD_FAILED',
        true,
        true
      );
      state.error = error;
      
      // Provide fallback behavior - use a default tenant if none exists
      if (state.tenants.length === 0) {
        const defaultTenant: Tenant = {
          id: 'default',
          name: 'Default Organization',
          domain: 'default',
          isDefault: true
        };
        state.tenants = [defaultTenant];
        state.currentTenant = defaultTenant;
        state.tenantPermissions = [];
      }
    },

    // Token Management
    setTokenStatus: (state, action: PayloadAction<TokenStatus>) => {
      logger.auth('Setting token status', { status: action.payload });
      state.tokenStatus = action.payload;
    },

    tokenRefreshSuccess: (state) => {
      logger.auth('Token refresh completed successfully');
      state.tokenStatus = TokenStatus.Valid;
      state.error = null;
      state.lastOperation = null;
    },

    tokenRefreshFailure: (state, action: PayloadAction<AuthError>) => {
      logger.auth('Token refresh failed', action.payload);
      state.tokenStatus = TokenStatus.Invalid;
      state.isAuthenticated = false;
      state.error = action.payload;
      state.lastOperation = null;
    },

    // Session Management
    initializeFromStorage: (state) => {
      logger.auth('Initializing authentication from storage');
      console.log('🔴 [authSlice] initializeFromStorage - Setting isLoading = true', {
        previousState: {
          isLoading: state.isLoading,
          isInitialized: state.isInitialized,
          isAuthenticated: state.isAuthenticated
        },
        timestamp: new Date().toISOString()
      });
      state.isLoading = true;
      state.error = null;
      state.lastOperation = 'initialize';
      console.log('🟡 [authSlice] initializeFromStorage - State updated', {
        newState: {
          isLoading: state.isLoading,
          isInitialized: state.isInitialized,
          isAuthenticated: state.isAuthenticated
        },
        timestamp: new Date().toISOString()
      });
    },

    initializationSuccess: (state, action: PayloadAction<LoginSuccessPayload>) => {
      logger.auth('Initialization from storage completed successfully');
      const { user, tenants, currentTenant, permissions } = action.payload;
      
      // Debug logging BEFORE state update
      console.log('🔵 [authSlice] initializationSuccess - BEFORE state update', {
        currentState: {
          isLoading: state.isLoading,
          isInitialized: state.isInitialized,
          isAuthenticated: state.isAuthenticated,
          hasUser: !!state.user
        },
        timestamp: new Date().toISOString()
      });
      
      // Debug logging for user data during initialization
      logger.auth('Storing user data in Redux state during initialization', {
        userId: user?.id,
        userName: user?.name,
        userEmail: user?.email,
        hasUser: !!user,
        hasName: !!user?.name,
        hasEmail: !!user?.email
      });
      
      state.isAuthenticated = true;
      state.isLoading = false;
      state.isInitialized = true;
      state.user = user;
      state.tenants = tenants;
      state.currentTenant = currentTenant;
      state.tenantPermissions = permissions;
      state.tokenStatus = TokenStatus.Valid;
      state.error = null;
      state.lastOperation = null;
      
      // Debug logging AFTER state update
      console.log('🟢 [authSlice] initializationSuccess - AFTER state update', {
        newState: {
          isLoading: state.isLoading,
          isInitialized: state.isInitialized,
          isAuthenticated: state.isAuthenticated,
          hasUser: !!state.user,
          userName: state.user?.name
        },
        timestamp: new Date().toISOString()
      });
    },

    initializationFailure: (state, action: PayloadAction<AuthError>) => {
      logger.auth('Initialization from storage failed', action.payload);
      state.isAuthenticated = false;
      state.isLoading = false;
      state.isInitialized = true;
      state.tokenStatus = TokenStatus.Invalid;
      state.error = action.payload;
      state.lastOperation = null;
    },

    // Loading State Management
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setInitializing: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.lastOperation = 'initialize';
      }
    },

    updateLoadingStates: (_state, _action: PayloadAction<Record<string, any>>) => {
      // This action is for external loading state coordination
      // The actual loading state is managed by the UI coordination service
      // This just triggers UI updates without causing circular dependencies
    },

    // Error Management
    setError: (state, action: PayloadAction<AuthError | null>) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    // System State Management
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = action.payload;
    },

    // Complete Reset (for testing and cleanup)
    reset: (_state) => {
      logger.auth('Resetting authentication state');
      return { ...initialState };
    },

    // Operation Tracking
    setLastOperation: (state, action: PayloadAction<string | null>) => {
      state.lastOperation = action.payload;
    },

    // Multi-Account Management Actions (migrated from legacy system)
    
    // Account switching actions
    startAccountSwitch: (state) => {
      logger.auth('Starting account switch');
      state.isAccountSwitching = true;
      state.accountSwitchError = null;
    },

    completeAccountSwitch: (state, action: PayloadAction<AccountSwitchPayload>) => {
      logger.auth('Account switch completed successfully');
      const { accountId, accounts } = action.payload;
      state.isAccountSwitching = false;
      state.activeAccountId = accountId;
      state.availableAccounts = accounts;
      state.accountSwitchError = null;
    },

    failAccountSwitch: (state, action: PayloadAction<string>) => {
      logger.auth('Account switch failed', action.payload);
      state.isAccountSwitching = false;
      state.accountSwitchError = action.payload;
    },

    // Account management actions
    startAddAccount: (state) => {
      logger.auth('Starting add account');
      state.isAddingAccount = true;
      state.addAccountError = null;
    },

    completeAddAccount: (state, action: PayloadAction<AddAccountPayload>) => {
      logger.auth('Add account completed successfully');
      const { accounts } = action.payload;
      state.isAddingAccount = false;
      state.availableAccounts = accounts;
      state.addAccountError = null;
    },

    failAddAccount: (state, action: PayloadAction<string>) => {
      logger.auth('Add account failed', action.payload);
      state.isAddingAccount = false;
      state.addAccountError = action.payload;
    },

    startRemoveAccount: (state) => {
      logger.auth('Starting remove account');
      state.isRemovingAccount = true;
      state.removeAccountError = null;
    },

    completeRemoveAccount: (state, action: PayloadAction<RemoveAccountPayload>) => {
      logger.auth('Remove account completed successfully');
      const { accounts, removedAccountId, newActiveAccountId, newSecret, isCompleteLogout } = action.payload;
      state.isRemovingAccount = false;
      state.availableAccounts = accounts;
      state.removeAccountError = null;
      
      // Handle complete logout case
      if (isCompleteLogout) {
        state.isAuthenticated = false;
        state.activeAccountId = null;
        state.availableAccounts = [];
      } else if (state.activeAccountId === removedAccountId) {
        // If the removed account was active, update to new active account
        state.activeAccountId = newActiveAccountId || null;
        state.isAuthenticated = !!newSecret;
      }
    },

    failRemoveAccount: (state, action: PayloadAction<string>) => {
      logger.auth('Remove account failed', action.payload);
      state.isRemovingAccount = false;
      state.removeAccountError = action.payload;
    },

    // Account list management
    updateAvailableAccounts: (state, action: PayloadAction<StoredAccount[]>) => {
      logger.auth('Updating available accounts');
      state.availableAccounts = action.payload;
    },

    updateAccountMetadata: (state, action: PayloadAction<AccountMetadataUpdate>) => {
      logger.auth('Updating account metadata');
      const { accountId, updates } = action.payload;
      const accountIndex = state.availableAccounts.findIndex(acc => acc.id === accountId);
      if (accountIndex !== -1) {
        state.availableAccounts[accountIndex] = {
          ...state.availableAccounts[accountIndex],
          ...updates
        };
      }
    },

    // Account validation
    setAccountValidation: (state, action: PayloadAction<ValidationPayload>) => {
      logger.auth('Setting account validation');
      const { validationTime, errors } = action.payload;
      state.lastValidationTime = validationTime;
      state.validationErrors = errors;
    },

    clearAccountValidationErrors: (state) => {
      logger.auth('Clearing account validation errors');
      state.validationErrors = {};
    },

    // Multi-account error management
    clearAccountSwitchError: (state) => {
      state.accountSwitchError = null;
    },

    clearAddAccountError: (state) => {
      state.addAccountError = null;
    },

    clearRemoveAccountError: (state) => {
      state.removeAccountError = null;
    },

    clearAllAccountErrors: (state) => {
      state.accountSwitchError = null;
      state.addAccountError = null;
      state.removeAccountError = null;
      state.validationErrors = {};
    },

    // Legacy compatibility alias
    clearAllErrors: (state) => {
      state.accountSwitchError = null;
      state.addAccountError = null;
      state.removeAccountError = null;
      state.validationErrors = {};
    },

    // Active account management
    setActiveAccount: (state, action: PayloadAction<string | null>) => {
      logger.auth('Setting active account', { accountId: action.payload });
      state.activeAccountId = action.payload;
    },

    // Update authentication secret (for token refresh) - legacy compatibility
    updateAuthSecret: (_state, _action: PayloadAction<any>) => {
      logger.auth('Update auth secret called (legacy compatibility)');
      // In the new system, auth secrets are not stored in Redux state
      // This action is kept for backward compatibility but does nothing
    },

    // Multi-account login (backward compatibility)
    loginWithAccount: (state, action: PayloadAction<{ 
      secret: any; 
      accountId: string;
      accounts: StoredAccount[];
    }>) => {
      logger.auth('Login with account completed');
      const { accountId, accounts } = action.payload;
      state.isAuthenticated = true;
      state.activeAccountId = accountId;
      state.availableAccounts = accounts;
      // Clear any previous errors
      state.accountSwitchError = null;
      state.addAccountError = null;
    }
  },
  extraReducers: (builder) => {
    // Handle initializeMultiAccountThunk
    builder
      .addMatcher(
        (action) => action.type === 'auth/initialize/fulfilled',
        (state, action: any) => {
          logger.auth('Multi-account initialization completed successfully');
          // Update available accounts
          state.availableAccounts = action.payload.accounts || [];
          
          // If there's an active account, set authentication state
          if (action.payload.activeAccount) {
            state.isAuthenticated = true;
            state.activeAccountId = action.payload.activeAccount.accountId;
          } else if (action.payload.accounts.length === 0) {
            // No accounts available, ensure logged out state
            state.isAuthenticated = false;
            state.activeAccountId = null;
          }
          
          // Clear any initialization errors
          state.accountSwitchError = null;
          state.addAccountError = null;
          state.removeAccountError = null;
        }
      )
      .addMatcher(
        (action) => action.type === 'auth/initialize/rejected',
        (state, action: any) => {
          logger.error('Multi-account initialization failed', action.payload);
          // Don't change authentication state on initialization failure
          // Just ensure we have empty accounts list if none exist
          if (state.availableAccounts.length === 0) {
            state.availableAccounts = [];
          }
        }
      );
    
    // Handle switchAccountThunk
    builder
      .addMatcher(
        (action) => action.type === 'auth/switchAccount/pending',
        (state) => {
          logger.auth('Account switch started');
          state.isAccountSwitching = true;
          state.accountSwitchError = null;
        }
      )
      .addMatcher(
        (action) => action.type === 'auth/switchAccount/fulfilled',
        (state, action: any) => {
          console.log('🔥 [authSlice] auth/switchAccount/fulfilled matcher triggered', {
            oldActiveAccountId: state.activeAccountId,
            newActiveAccountId: action.payload.accountId,
            payload: action.payload
          });
          logger.auth('Account switch completed successfully');
          state.isAccountSwitching = false;
          state.isAuthenticated = true;
          state.activeAccountId = action.payload.accountId;
          state.availableAccounts = action.payload.accounts;
          state.accountSwitchError = null;
          console.log('🔥 [authSlice] Redux state updated', {
            activeAccountId: state.activeAccountId,
            isAuthenticated: state.isAuthenticated
          });
        }
      )
      .addMatcher(
        (action) => action.type === 'auth/switchAccount/rejected',
        (state, action: any) => {
          logger.auth('Account switch failed', action.payload);
          state.isAccountSwitching = false;
          state.accountSwitchError = action.payload || 'Failed to switch account';
        }
      );
    
    // Handle addAccountThunk
    builder
      .addMatcher(
        (action) => action.type === 'auth/addAccount/pending',
        (state) => {
          logger.auth('Add account started');
          state.isAddingAccount = true;
          state.addAccountError = null;
        }
      )
      .addMatcher(
        (action) => action.type === 'auth/addAccount/fulfilled',
        (state, action: any) => {
          logger.auth('Add account completed successfully');
          state.isAddingAccount = false;
          state.availableAccounts = action.payload.accounts;
          state.addAccountError = null;
        }
      )
      .addMatcher(
        (action) => action.type === 'auth/addAccount/rejected',
        (state, action: any) => {
          logger.auth('Add account failed', action.payload);
          state.isAddingAccount = false;
          state.addAccountError = action.payload || 'Failed to add account';
        }
      );
    
    // Handle removeAccountThunkRobust
    builder
      .addMatcher(
        (action) => action.type === 'auth/removeAccountRobust/pending',
        (state) => {
          logger.auth('Remove account started');
          state.isRemovingAccount = true;
          state.removeAccountError = null;
        }
      )
      .addMatcher(
        (action) => action.type === 'auth/removeAccountRobust/fulfilled',
        (state, action: any) => {
          logger.auth('Remove account completed successfully');
          state.isRemovingAccount = false;
          state.availableAccounts = action.payload.accounts;
          state.removeAccountError = null;
          
          // Handle complete logout case
          if (action.payload.isCompleteLogout) {
            state.isAuthenticated = false;
            state.activeAccountId = null;
            state.availableAccounts = [];
          } else if (state.activeAccountId === action.payload.removedAccountId) {
            // If the removed account was active, update to new active account
            state.activeAccountId = action.payload.newActiveAccountId || null;
            state.isAuthenticated = !!action.payload.newSecret;
          }
        }
      )
      .addMatcher(
        (action) => action.type === 'auth/removeAccountRobust/rejected',
        (state, action: any) => {
          logger.auth('Remove account failed', action.payload);
          state.isRemovingAccount = false;
          state.removeAccountError = action.payload || 'Failed to remove account';
        }
      );
    
    // Handle refreshAccountsThunk
    builder.addMatcher(
      (action) => action.type === 'auth/refreshAccounts/fulfilled',
      (state, action: any) => {
        logger.auth('Refresh accounts completed');
        state.availableAccounts = action.payload;
      }
    );
    
    // Handle validateAccountsThunk
    builder.addMatcher(
      (action) => action.type === 'auth/validateAccounts/fulfilled',
      (state, action: any) => {
        logger.auth('Validate accounts completed');
        state.lastValidationTime = action.payload.validationTime;
        state.validationErrors = action.payload.errors;
      }
    );
  }
});

// Export actions
export const {
  startLogin,
  startLogout,
  startTenantSwitch,
  startTokenRefresh,
  startAccountSwitch,
  loginSuccess,
  loginFailure,
  logoutSuccess,
  logoutFailure,
  initializeFromStorage,
  initializationSuccess,
  initializationFailure,
  setUser,
  setUserPermissions,
  clearUserPermissions,
  updateUser,
  startAccountSwitchWorkflow,
  accountSwitchSuccess,
  accountSwitchFailure,
  completeAccountSwitch,
  failAccountSwitch,
  startAddAccount,
  completeAddAccount,
  failAddAccount,
  startRemoveAccount,
  completeRemoveAccount,
  failRemoveAccount,
  accountRemoved,
  updateAccountMetadata,
  updateAvailableAccounts,
  setActiveAccount,
  loginWithAccount,
  updateAuthSecret,
  clearError,
  clearAccountSwitchError,
  clearAddAccountError,
  clearRemoveAccountError,
  clearAllErrors,
  clearAccountValidationErrors,
  setTokenStatus,
  tokenRefreshSuccess,
  tokenRefreshFailure,
  tenantSwitchSuccess,
  tenantSwitchFailure,
  updateLoadingStates,
  setLastOperation
} = authSlice.actions;

// Export reducer
export default authSlice.reducer;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectIsLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectIsInitialized = (state: { auth: AuthState }) => state.auth.isInitialized;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectTenants = (state: { auth: AuthState }) => state.auth.tenants;
export const selectCurrentTenant = (state: { auth: AuthState }) => state.auth.currentTenant;
export const selectTenantPermissions = (state: { auth: AuthState }) => state.auth.tenantPermissions;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectTokenStatus = (state: { auth: AuthState }) => state.auth.tokenStatus;
export const selectLastOperation = (state: { auth: AuthState }) => state.auth.lastOperation;

// Computed selectors
export const selectUserDisplayName = (state: { auth: AuthState }) => {
  const user = state.auth.user;
  return user ? user.name || user.email || 'Unknown User' : null;
};

export const selectHasMultipleTenants = (state: { auth: AuthState }) => 
  state.auth.tenants.length > 1;

export const selectCanRetryLastOperation = (state: { auth: AuthState }) => {
  const error = state.auth.error;
  return error ? error.retryable : false;
};

export const selectIsOperationInProgress = (state: { auth: AuthState }) => 
  state.auth.isLoading;

export const selectAuthenticationReady = (state: { auth: AuthState }) => 
  state.auth.isInitialized && !state.auth.isLoading;

// Validation-aware selectors
export const selectIsUserDataComplete = (state: { auth: AuthState }) => {
  const user = state.auth.user;
  if (!user) return false;
  
  // Check required fields
  if (!user.id || !user.email || !user.name) return false;
  
  // Validate user ID can be converted to number
  const numericId = parseInt(user.id, 10);
  return !isNaN(numericId) && numericId > 0;
};

export const selectValidUserId = (state: { auth: AuthState }) => {
  const user = state.auth.user;
  if (!user?.id) return null;
  
  const numericId = parseInt(user.id, 10);
  return !isNaN(numericId) && numericId > 0 ? numericId : null;
};

export const selectIsReadyForApiOperations = (state: { auth: AuthState }) => {
  return state.auth.isAuthenticated && 
         state.auth.isInitialized && 
         !state.auth.isLoading &&
         selectIsUserDataComplete(state);
};

export const selectAuthValidationMetadata = (state: { auth: AuthState }) => {
  const user = state.auth.user;
  const isAuthenticated = state.auth.isAuthenticated;
  const isInitialized = state.auth.isInitialized;
  const isLoading = state.auth.isLoading;
  
  return {
    isAuthenticated,
    isInitialized,
    isLoading,
    hasUser: !!user,
    hasUserId: !!user?.id,
    hasUserEmail: !!user?.email,
    hasUserName: !!user?.name,
    isUserIdValid: selectValidUserId(state) !== null,
    isUserDataComplete: selectIsUserDataComplete(state),
    isReadyForApiOperations: selectIsReadyForApiOperations(state),
    validUserId: selectValidUserId(state)
  };
};

// Multi-Account Selectors (migrated from legacy system)
export const selectActiveAccountId = (state: { auth: AuthState }) => state.auth.activeAccountId;
export const selectAvailableAccounts = (state: { auth: AuthState }) => state.auth.availableAccounts;
export const selectIsAccountSwitching = (state: { auth: AuthState }) => state.auth.isAccountSwitching;
export const selectAccountSwitchError = (state: { auth: AuthState }) => state.auth.accountSwitchError;
export const selectIsAddingAccount = (state: { auth: AuthState }) => state.auth.isAddingAccount;
export const selectAddAccountError = (state: { auth: AuthState }) => state.auth.addAccountError;
export const selectIsRemovingAccount = (state: { auth: AuthState }) => state.auth.isRemovingAccount;
export const selectRemoveAccountError = (state: { auth: AuthState }) => state.auth.removeAccountError;
export const selectValidationErrors = (state: { auth: AuthState }) => state.auth.validationErrors;
export const selectLastValidationTime = (state: { auth: AuthState }) => state.auth.lastValidationTime;

// Multi-Account Computed Selectors
export const selectActiveAccount = (state: { auth: AuthState }) => {
  const { activeAccountId, availableAccounts } = state.auth;
  return activeAccountId 
    ? availableAccounts.find(acc => acc.id === activeAccountId) || null
    : null;
};

export const selectAccountCount = (state: { auth: AuthState }) => 
  state.auth.availableAccounts.length;

export const selectHasMultipleAccounts = (state: { auth: AuthState }) => 
  state.auth.availableAccounts.length > 1;

export const selectAccountById = (accountId: string) => (state: { auth: AuthState }) =>
  state.auth.availableAccounts.find(acc => acc.id === accountId) || null;

export const selectAccountsNeedingAttention = (state: { auth: AuthState }) =>
  Object.keys(state.auth.validationErrors).length;

export const selectIsAnyAccountOperationInProgress = (state: { auth: AuthState }) =>
  state.auth.isAccountSwitching || state.auth.isAddingAccount || state.auth.isRemovingAccount;

// Combined operation status selector
export const selectIsAnyOperationInProgress = (state: { auth: AuthState }) => 
  state.auth.isLoading || selectIsAnyAccountOperationInProgress(state);

// Export types
export type { AuthState };
